import { useMemo, useState } from "react";
import { Edit, Settings2, Trash2 } from "lucide-react";

import { API_BASE } from "@/config/api";
import { fetchWithAuth } from "@/lib/api";
import {
  BOOK_TYPE_OPTIONS,
  DEFAULT_BOOK_TYPE,
  getBookTypeDotClass,
  getBookTypeLabel,
  shouldShowBookMode,
} from "@/lib/book-types";
import type { Book, BookType, TWRMode } from "@/schemas";
import { DeleteConfirmationDialog } from "@/components/ui/delete-confirmation-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface ManagePortfoliosModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  books: Book[];
  selectedBookId: string;
  onBookSelect: (bookId: string) => void;
  onCreatePortfolio: () => void;
  onPortfoliosChanged: () => Promise<void> | void;
}

export function ManagePortfoliosModal({
  open,
  onOpenChange,
  books,
  selectedBookId,
  onBookSelect,
  onCreatePortfolio,
  onPortfoliosChanged,
}: ManagePortfoliosModalProps) {
  const [editingBook, setEditingBook] = useState<Book | null>(null);
  const [editPortfolioName, setEditPortfolioName] = useState("");
  const [editPortfolioType, setEditPortfolioType] =
    useState<BookType>(DEFAULT_BOOK_TYPE);
  const [editPortfolioMode, setEditPortfolioMode] =
    useState<TWRMode>("advanced");

  const [bookToDelete, setBookToDelete] = useState<Book | null>(null);

  const sortedBooks = useMemo(
    () => [...books].sort((a, b) => a.name.localeCompare(b.name)),
    [books],
  );

  const handleStartEdit = (book: Book) => {
    setEditingBook(book);
    setEditPortfolioName(book.name);
    setEditPortfolioType(book.book_type ?? DEFAULT_BOOK_TYPE);
    setEditPortfolioMode(book.twr_mode ?? "advanced");
  };

  const handleSaveEdit = async () => {
    if (!editingBook || !editPortfolioName.trim()) return;

    try {
      const response = await fetchWithAuth(
        `${API_BASE}/books/${editingBook.book_id}`,
        {
          method: "PUT",
          body: JSON.stringify({
            name: editPortfolioName.trim(),
            provider: editingBook.provider,
            book_type: editPortfolioType,
            twr_mode: editPortfolioMode,
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to update portfolio");
      }

      await onPortfoliosChanged();
      setEditingBook(null);
    } catch (error) {
      console.error("Error updating portfolio:", error);
    }
  };

  const handleDeletePortfolio = async () => {
    if (!bookToDelete) return;

    try {
      const response = await fetchWithAuth(
        `${API_BASE}/books/${bookToDelete.book_id}`,
        {
          method: "DELETE",
        },
      );

      if (!response.ok) {
        throw new Error("Failed to delete portfolio");
      }

      await onPortfoliosChanged();

      if (selectedBookId === bookToDelete.book_id) {
        onBookSelect("global");
      }
    } catch (error) {
      console.error("Error deleting portfolio:", error);
      throw error;
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="h-5 w-5" />
              Manage Portfolios
            </DialogTitle>
            <DialogDescription>
              Create, rename, and organize your portfolios without leaving this
              page.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-xl border border-border/50">
            <div className="border-b border-border/50 px-4 py-3">
              <h3 className="text-sm font-semibold">Existing Portfolios</h3>
            </div>

            <div className="divide-y divide-border/40">
              {sortedBooks.length === 0 ? (
                <div className="px-4 py-8 text-sm text-muted-foreground">
                  No portfolio yet. Use the create action from the selector
                  menu.
                </div>
              ) : (
                sortedBooks.map((book) => (
                  <div
                    key={book.book_id}
                    className="group flex items-center justify-between gap-3 px-4 py-3 transition-colors hover:bg-muted/20"
                  >
                    <button
                      type="button"
                      className="text-left"
                      onClick={() => onBookSelect(book.book_id)}
                    >
                      <p className="text-sm font-medium flex items-center gap-2">
                        <span
                          className={`h-2.5 w-2.5 rounded-full ${getBookTypeDotClass(book.book_type)}`}
                        />
                        {book.name}
                        <span className="text-xs text-muted-foreground">
                          {getBookTypeLabel(book.book_type)}
                        </span>
                      </p>
                    </button>

                    <div className="flex items-center gap-2">
                      {selectedBookId === book.book_id && (
                        <Badge variant="secondary">Selected</Badge>
                      )}
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 border-border/50 bg-background/60 opacity-90 transition-all hover:bg-background group-hover:opacity-100"
                        onClick={() => handleStartEdit(book)}
                        aria-label={`Edit ${book.name}`}
                      >
                        <Edit className="h-4 w-4 text-muted-foreground" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 border-border/50 bg-background/60 text-destructive opacity-90 transition-all hover:border-destructive/50 hover:bg-destructive/5 hover:text-destructive group-hover:opacity-100"
                        onClick={() => setBookToDelete(book)}
                        aria-label={`Delete ${book.name}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => {
                onOpenChange(false);
                onCreatePortfolio();
              }}
            >
              Create Portfolio
            </Button>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!editingBook}
        onOpenChange={(nextOpen) => !nextOpen && setEditingBook(null)}
      >
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>Edit Portfolio</DialogTitle>
            <DialogDescription>
              Update name, type, and calculation rules for this portfolio.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="edit-portfolio-name">Portfolio Name</Label>
              <Input
                id="edit-portfolio-name"
                value={editPortfolioName}
                onChange={(event) => setEditPortfolioName(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-portfolio-type">Portfolio Type</Label>
              <Select
                value={editPortfolioType}
                onValueChange={(value) =>
                  setEditPortfolioType(value as BookType)
                }
              >
                <SelectTrigger id="edit-portfolio-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BOOK_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {shouldShowBookMode(editPortfolioType) ? (
              <div className="space-y-2">
                <Label htmlFor="edit-portfolio-mode">Calculation Mode</Label>
                <Select
                  value={editPortfolioMode}
                  onValueChange={(value) =>
                    setEditPortfolioMode(value as TWRMode)
                  }
                >
                  <SelectTrigger id="edit-portfolio-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="advanced">
                      Advanced - includes cash flows in performance calculations
                    </SelectItem>
                    <SelectItem value="simple">
                      Simple - focuses on buy and sell activity only
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : null}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingBook(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={!editPortfolioName.trim()}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteConfirmationDialog
        open={!!bookToDelete}
        onOpenChange={(nextOpen) => !nextOpen && setBookToDelete(null)}
        title="Delete Portfolio"
        description="Are you sure you want to delete this portfolio and all its associated transactions?"
        itemName={bookToDelete?.name ?? ""}
        onConfirm={handleDeletePortfolio}
        confirmButtonText="Delete Portfolio"
      />
    </>
  );
}
